import { isEmpty } from 'lodash';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useProductCostingState } from '../ProductCostingContext';

/** The redux slices this hook reads. Declared narrowly, per the CostingTsx/MyTodo convention. */
interface ShowHideRootState {
  UserDetails: { userRole?: string; isSupplierLoggedIn?: boolean };
  BOMConfigDetails: { isProductArchived?: boolean };
  newProductCostingData: { productLengths?: unknown[] };
}

/** The logged-in user blob cached in localStorage; only `lengthVisible` is read here. */
interface CachedUserInfo {
  lengthVisible?: boolean | null;
}

export interface UseShowHideSectionResult {
  hideSection: boolean;
  isSupplier: boolean;
  isSupplierView: boolean;
  statusState: boolean;
  hideInviteBtn: boolean;
  onLoadDisable: boolean;
  isRefreshBtnACtive: boolean;
  markAsCompleteState: boolean;
  isDisabled: boolean;
  controlAmberColour: boolean;
  hideLengthCol: boolean;
  hideColourCol: boolean;
  hideSizeCol: boolean;
  hideLengthSysMaster: boolean;
}

/**
 * Resolves every "should this be visible / editable" flag the costing tables share, from the
 * combination of product archive state, costing status, supplier vs company user, and the
 * colourway / size+length breakdown toggles.
 */
export default function useShowHideSectionHooks(): UseShowHideSectionResult {
  const { productCostingstate, internalState, colSizeLengthToggleState } = useProductCostingState();
  const { colourWayToggle, sizeLengthToggle } = colSizeLengthToggleState;
  const { productid } = useParams<{ productid?: string }>();

  const {
    UserDetails: { userRole, isSupplierLoggedIn },
    BOMConfigDetails: { isProductArchived },
    newProductCostingData: { productLengths },
  } = useSelector((prevState: ShowHideRootState) => prevState);

  const {
    status,
    isGlobalUrl,
    productInfo: { supplierId },
  } = productCostingstate;
  const hideSection = Boolean(isProductArchived) || !isEmpty(productid);
  const isSupplier = userRole === 'Supplier' || isGlobalUrl;

  /**
   * True for both supplier entry points: the logged-in (integrated) supplier and
   * the credential-less (non-integrated) magic link. Use this to gate any control
   * that is internal-only, so both supplier views stay identical.
   */
  const isSupplierView = Boolean(isSupplierLoggedIn) || Boolean(isGlobalUrl);
  const statusState = Boolean(isProductArchived || internalState?.markAsComplete || (status !== 'Complete' && isSupplier ? false : !!isSupplier));

  const hideInviteBtn = Boolean(isProductArchived) || (hideSection && !isSupplier && status === 'Draft');

  const onLoadDisable = !supplierId;

  const isRefreshBtnACtive = hideSection && ['Pending', 'Draft', 'Partially Complete'].includes(status) && !isSupplier && !isGlobalUrl;

  const markAsCompleteState = internalState?.markAsComplete;
  const isDisabled = Boolean(isProductArchived || internalState?.markAsComplete || userRole === 'Supplier' || isGlobalUrl);
  const controlAmberColour = !isSupplierLoggedIn && !isGlobalUrl;
  const userData = JSON.parse(localStorage.getItem('loggedInUserInfo') as string) as CachedUserInfo | null;
  const hideColourCol = Boolean(internalState?.hideColourCol && colourWayToggle);
  const hideSizeCol = Boolean(internalState?.hideSizeCol && sizeLengthToggle);
  const hideLengthCol = Boolean(
    sizeLengthToggle && userData?.lengthVisible && userData?.lengthVisible !== null && userData?.lengthVisible !== undefined && (productLengths?.length ?? 0) > 0,
  );
  const hideLengthSysMaster = Boolean(userData?.lengthVisible && userData?.lengthVisible !== null && userData?.lengthVisible !== undefined);
  return {
    hideSection,
    isSupplier,
    isSupplierView,
    statusState,
    hideInviteBtn,
    onLoadDisable,
    isRefreshBtnACtive,
    markAsCompleteState,
    isDisabled,
    controlAmberColour,
    hideLengthCol,
    hideColourCol,
    hideSizeCol,
    hideLengthSysMaster,
  };
}
